import { trailersAPI } from "components/Apis/assets";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Nav, NavItem, NavLink } from "reactstrap";
import { fetchTrailers } from "redux/asssets/trailersSlice";
import TrailerRow from "./subcomponents/safety-subcomponents/trailerRow";

const deriveStateLabel = (trailer) => {
    const latest = trailer?.latest_update || trailer?.latest_update || trailer?.latest_location;
    const locationState = latest?.state || latest?.raw?.location?.address?.state;
    return locationState || trailer?.state || "Unknown";
};

const Trailers = () => {
    const dispatch = useDispatch();

    const { list: trailers, loading } = useSelector((state) => state.trailers);
    const { searchTerm } = useSelector((state) => state.archiveSearch);
    const isSearching = Boolean(searchTerm?.trim());

    const groupByState = (items) => {
        if (!items?.length) return [];

        const groups = items.reduce((acc, trailer) => {
            const stateName = deriveStateLabel(trailer);
            if (!acc[stateName]) acc[stateName] = [];
            acc[stateName].push(trailer);
            return acc;
        }, {});

        return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
    };

    const groupedAvailable = useMemo(
        () => groupByState(trailers?.filter((trailer) => trailer.available)),
        [trailers]
    );

    const groupedUnavailable = useMemo(
        () => groupByState(trailers?.filter((trailer) => !trailer.available)),
        [trailers]
    );

    const groupedSearchResults = useMemo(() => groupByState(trailers), [trailers]);

    const [activeStates, setActiveStates] = useState({});
    const [togglingId, setTogglingId] = useState(null);
    const [notesSavingId, setNotesSavingId] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [showUnavailable, setShowUnavailable] = useState(false);

    useEffect(() => {
        dispatch(fetchTrailers({ search: searchTerm, available: showUnavailable || searchTerm ? null : true }));
    }, [dispatch, searchTerm, showUnavailable]);


    useEffect(() => {
        setActiveStates((prev) => {
            const next = { ...prev };
            const configs = [
                { key: "available", groups: groupedAvailable },
                { key: "unavailable", groups: groupedUnavailable },
            ];
            if (isSearching) {
                configs.push({ key: "search", groups: groupedSearchResults });
            } else {
                delete next.search;
            }

            configs.forEach(({ key, groups }) => {
                if (!groups.length) {
                    delete next[key];
                    return;
                }
                const hasExisting = prev[key] && groups.some(([name]) => name === prev[key]);
                next[key] = hasExisting ? prev[key] : groups[0][0];
            });
            return next;
        });
    }, [groupedAvailable, groupedUnavailable, groupedSearchResults, isSearching]);

    const handleToggleAvailability = async (trailer) => {
        if (!trailer) return;
        setActionError(null);
        setTogglingId(trailer.id);
        try {
            await trailersAPI.changeStatus(trailer.id, !trailer.available);
            dispatch(fetchTrailers());
        } catch (error) {
            const message = error.response?.data?.detail || "Unable to update trailer availability.";
            setActionError(message);
            // eslint-disable-next-line no-console
            console.error(error);
        } finally {
            setTogglingId(null);
        }
    };

    const handleSaveNotes = async (trailer, notes) => {
        if (!trailer) return;
        setActionError(null);
        setNotesSavingId(trailer.id);
        try {
            await trailersAPI.patchNotes(trailer.id, notes);
            dispatch(fetchTrailers());
        } catch (error) {
            const message = error.response?.data?.detail || "Unable to update trailer notes.";
            setActionError(message);
            // eslint-disable-next-line no-console
            console.error(error);
        } finally {
            setNotesSavingId(null);
        }
    };

    const hasTrailers = isSearching
        ? groupedSearchResults.length > 0
        : groupedAvailable.length > 0 || groupedUnavailable.length > 0;

    if (!hasTrailers) {
        return (
            <div className="trailers-empty">
                {loading
                    ? "Loading trailers..."
                    : isSearching
                        ? "No trailers match your search."
                        : "No trailers available."}
            </div>
        );
    }

    const renderStateTabs = (groups, availabilityKey) => (
        <div className="state-tabs">
            <Nav tabs className="state-tabs-nav">
                {groups.map(([geofenceName, group]) => {
                    const countLabel = `${group.length}`;
                    const isActive = activeStates[availabilityKey] === geofenceName;

                    return (
                        <NavItem key={`${availabilityKey}-${geofenceName}`}>
                            <NavLink
                                className={isActive ? "active" : ""}
                                onClick={() =>
                                    setActiveStates((prev) => ({
                                        ...prev,
                                        [availabilityKey]: geofenceName,
                                    }))
                                }
                            >
                                <span className="state-tab__name">{geofenceName}</span>
                                <span className="state-tab__count">{countLabel}</span>
                            </NavLink>
                        </NavItem>
                    );
                })}
            </Nav>
            <div className="state-tab-panel">
                {(() => {
                    const activeName = activeStates[availabilityKey] || groups[0][0];
                    const activeTrailers =
                        groups.find(([name]) => name === activeName)?.[1] || [];

                    return (
                        <table className="briefing-table sub-section-table">
                            <thead>
                                <tr>
                                    <th style={{ width: "15%" }}>Trailer</th>
                                    <th style={{ width: "15%" }}>Specs</th>
                                    <th style={{ width: "15%" }}>Sylectus</th>
                                    <th style={{ width: "10%" }}>Samsara</th>
                                    <th style={{ width: "45%" }}>Control</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activeTrailers.map((trailer) => (
                                    <TrailerRow
                                        key={trailer.id}
                                        trailer={trailer}
                                        onToggleAvailability={handleToggleAvailability}
                                        isUpdating={togglingId === trailer.id}
                                        onSaveNotes={handleSaveNotes}
                                        isSavingNotes={notesSavingId === trailer.id}
                                    />
                                ))}
                            </tbody>
                        </table>
                    );
                })()}
            </div>
        </div>
    );

    return (
        <div className="trailers-grouped">
            {actionError && (
                <div className="trailers-error">
                    {actionError}
                </div>
            )}

            {isSearching ? (
                <section className="availability-section">
                    <header className="availability-header">
                        <h3>Search Results</h3>
                        <span>
                            {groupedSearchResults.reduce((sum, [, list]) => sum + list.length, 0)} total
                        </span>
                    </header>

                    {groupedSearchResults.length === 0 ? (
                        <div className="trailers-empty compact">No trailers match your search.</div>
                    ) : (
                        renderStateTabs(groupedSearchResults, "search")
                    )}
                </section>
            ) : (
                <>
                    <section className="availability-section">
                        <header className="availability-header">
                            <h3>Available Trailers</h3>
                            <span>
                                {groupedAvailable.reduce((sum, [, list]) => sum + list.length, 0)} total
                            </span>
                        </header>

                        {groupedAvailable.length === 0 ? (
                            <div className="trailers-empty compact">No trailers in this category.</div>
                        ) : (
                            renderStateTabs(groupedAvailable, "available")
                        )}
                        <hr style={{ margin: "10px 0px" }} />
                    </section>

                    <section className="availability-section">
                        <header className="availability-header">
                            <h3>Unavailable Trailers</h3>
                            <button
                                type="button"
                                className="availability-collapse-toggle"
                                onClick={() => setShowUnavailable((prev) => !prev)}
                                aria-expanded={showUnavailable}
                                style={{
                                    borderRadius: "999px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "0.45rem",
                                    padding: "0.35rem 0.95rem",
                                    fontSize: "0.85rem",
                                    fontWeight: 600,
                                    border: "1px solid rgba(15, 23, 42, 0.2)",
                                    backgroundColor: showUnavailable ? "#111827" : "#ffffff",
                                    color: showUnavailable ? "#ffffff" : "#0f172a",
                                    boxShadow: "0 4px 12px rgba(15, 23, 42, 0.15)",
                                    transition: "all 160ms ease",
                                }}
                            >
                                <span
                                    aria-hidden="true"
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: "1.25rem",
                                        height: "1.25rem",
                                        borderRadius: "50%",
                                        backgroundColor: showUnavailable
                                            ? "rgba(255, 255, 255, 0.18)"
                                            : "rgba(15, 23, 42, 0.08)",
                                    }}
                                >
                                    <i
                                        className={`fa ${showUnavailable ? "fa-chevron-up" : "fa-chevron-down"
                                            }`}
                                    />
                                </span>
                                <span
                                    style={{
                                        color: showUnavailable ? "#ffffff" : "#0f172a",
                                        fontWeight: 600,
                                    }}
                                >
                                    {showUnavailable ? "Hide unavailable" : "Show unavailable"}
                                </span>
                            </button>
                        </header>

                        {groupedUnavailable.length === 0 && showUnavailable ? (
                            loading ? (
                                <div className="trailers-empty compact">Loading trailers...</div>
                            ) : (
                                <div className="trailers-empty compact">No trailers in this category.</div>
                            )
                        ) : !showUnavailable ? (
                            <div className="trailers-collapse-placeholder">
                                Click &ldquo;Show&rdquo; to view unavailable trailers.
                            </div>
                        ) : (
                            renderStateTabs(groupedUnavailable, "unavailable")
                        )}
                    </section>
                </>
            )}
        </div>
    );
};

export default Trailers;
